/**
 * ExplorationZoneDetailPage — J7-W6 (PRD §12.3.10 task 8 / §9.12.3).
 *
 * Read-only view of one zone (zones are immutable except for status). Surfaces
 * every field, the linked Decision Note, the expiry countdown, and the same
 * Revoke + Close → Decision Note actions as the list page.
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Landmark, ArrowLeft } from 'lucide-react';
import { t } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { showToast } from '@/components/ui/Toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  useRorosExplorationZone,
  useRorosExplorationZoneRevoke,
  useRorosExplorationZoneClose,
} from '@/hooks/roros/governance';
import { useRorosDomains } from '@/hooks/roros/domains';
import { formatDate } from '@/lib/localeFormatters';
import { DecisionFormDialog } from '../decisions/DecisionFormDialog';
import type { RorosExplorationZoneDTO } from '@/services/roros/governance.service';
import type { RorosDomainDTO } from '@/types/roros/domains';

function statusBadge(status: string): React.ReactNode {
  switch (status) {
    case 'active':
      return <Badge variant="success">{t('Active')}</Badge>;
    case 'expired':
      return <Badge variant="secondary">{t('Expired')}</Badge>;
    case 'revoked':
      return <Badge variant="error">{t('Revoked')}</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

function expiryCountdown(zone: RorosExplorationZoneDTO): React.ReactNode {
  if (zone.status === 'revoked') {
    return <span className="text-xs text-muted-foreground">{t('Revoked — no countdown')}</span>;
  }
  const expiresAt = new Date(zone.expiresAt).getTime();
  const now = Date.now();
  const daysRemaining = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
  if (daysRemaining < 0 || zone.status === 'expired') {
    return <Badge variant="error">{t('Expired')}</Badge>;
  }
  if (daysRemaining <= 7) {
    return (
      <Badge variant="warning">
        {daysRemaining} {t('days left')}
      </Badge>
    );
  }
  return (
    <Badge variant="success">
      {daysRemaining} {t('days left')}
    </Badge>
  );
}

export function ExplorationZoneDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const zoneQuery = useRorosExplorationZone(id);
  const domainsQuery = useRorosDomains();
  const revokeMutation = useRorosExplorationZoneRevoke();
  const closeMutation = useRorosExplorationZoneClose();

  const [revokeOpen, setRevokeOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);

  const zone = zoneQuery.data as RorosExplorationZoneDTO | undefined;

  const domainName =
    zone &&
    ((domainsQuery.data as RorosDomainDTO[] | undefined) ?? []).find((d) => d.id === zone.domainId)?.name;

  const handleRevoke = () => {
    revokeMutation.mutate(id, {
      onSuccess: () => {
        showToast.success(t('Exploration zone revoked'));
        setRevokeOpen(false);
        zoneQuery.refetch();
      },
      onError: (err: Error) => {
        showToast.error(err.message || t('Failed to revoke exploration zone'));
      },
    });
  };

  return (
    <RorosPage
      title={t('Exploration Zone')}
      icon={<Landmark className="h-5 w-5" aria-hidden />}
      actions={
        <Button
          variant="ghost"
          leftIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/roros/governance/exploration-zones')}
          data-testid="exploration-zone-back-button"
        >
          {t('Back to list')}
        </Button>
      }
    >
      <QueryPage query={zoneQuery} emptyMessage={t('Exploration zone not found.')}>
        {(z) => {
          const data = z as RorosExplorationZoneDTO;
          return (
            <div className="mt-4 space-y-4" data-testid="exploration-zone-detail">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-lg">{data.title}</CardTitle>
                    <div className="flex items-center gap-2">
                      {statusBadge(data.status)}
                      {expiryCountdown(data)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('Description')}</p>
                    <p className="text-sm whitespace-pre-wrap">{data.description}</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">{t('Domain')}</p>
                      <p className="text-sm">{domainName ?? data.domainId.slice(0, 8)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('Expires at')}</p>
                      <p className="text-sm">
                        {data.expiresAt ? formatDate(new Date(data.expiresAt)) : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('Created at')}</p>
                      <p className="text-sm">{formatDate(new Date(data.createdAt))}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('Created by')}</p>
                      <p className="text-sm font-mono text-xs">
                        {data.createdBy ? data.createdBy.slice(0, 12) + '…' : '—'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">{t('Decision Note')}</p>
                    {data.decisionNoteId ? (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0"
                        onClick={() => navigate(`/roros/decisions?openId=${data.decisionNoteId}`)}
                        data-testid="exploration-zone-decision-link"
                      >
                        {data.decisionNoteId.slice(0, 12)}…
                      </Button>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('—')}</p>
                    )}
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('Scope')}</p>
                    <pre className="text-xs bg-muted/40 rounded p-2 overflow-x-auto">
                      {JSON.stringify(data.scope ?? {}, null, 2)}
                    </pre>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('Excluded categories')}</p>
                    {(data.excludedCategories ?? []).length === 0 ? (
                      <p className="text-sm text-muted-foreground">{t('—')}</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {(data.excludedCategories ?? []).map((c, i) => (
                          <Badge key={i} variant="outline">{String(c)}</Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {data.status === 'active' && (
                    <div className="flex items-center gap-2 pt-3 border-t border-border">
                      <Button
                        variant="outline"
                        onClick={() => setCloseOpen(true)}
                        data-testid="exploration-zone-close-button"
                      >
                        {t('Close → Decision Note')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setRevokeOpen(true)}
                        data-testid="exploration-zone-revoke-button"
                      >
                        {t('Revoke')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <ConfirmDialog
                isOpen={revokeOpen}
                onClose={() => setRevokeOpen(false)}
                onConfirm={handleRevoke}
                title={t('Revoke this exploration zone?')}
                description={t('Revoking immediately ends the zone. This cannot be undone (GOV-05 audit event recorded).')}
                variant="destructive"
                confirmText={t('Revoke')}
                isLoading={revokeMutation.isPending}
              />

              <DecisionFormDialog
                isOpen={closeOpen}
                onClose={() => setCloseOpen(false)}
                defaultImpact="trunk"
                onCreated={(decisionId) => {
                  if (decisionId) {
                    closeMutation.mutate(
                      { id, decisionNoteId: decisionId },
                      {
                        onSuccess: () => {
                          showToast.success(t('Zone closed and linked to Decision Note.'));
                          setCloseOpen(false);
                          zoneQuery.refetch();
                        },
                        onError: (err: any) => {
                          showToast.error(err?.message ?? t('Failed to back-link zone to Decision Note.'));
                          setCloseOpen(false);
                          zoneQuery.refetch();
                        },
                      },
                    );
                  } else {
                    showToast.info(t('Decision Note created. Revoke the zone manually if needed.'));
                    setCloseOpen(false);
                    zoneQuery.refetch();
                  }
                }}
              />
            </div>
          );
        }}
      </QueryPage>
    </RorosPage>
  );
}
